import { ClinicalAlert } from '../types';
import { supabase, isSupabaseAvailable } from './supabaseClient';

type AlertRow = {
  id: string;
  clinician_id: string;
  patient_id: string | null;
  test_result_id: string;
  test_code: string;
  patient_name: string;
  severity: string;
  message: string;
  triggered_score: number | null;
  triggered_at: string;
  read_at: string | null;
  dismissed_at: string | null;
};

function dbToAlert(row: AlertRow): ClinicalAlert {
  return {
    id:             row.id,
    clinicianId:    row.clinician_id,
    patientId:      row.patient_id,
    testResultId:   row.test_result_id,
    testCode:       row.test_code,
    patientName:    row.patient_name,
    severity:       row.severity as ClinicalAlert['severity'],
    message:        row.message,
    triggeredScore: row.triggered_score,
    triggeredAt:    row.triggered_at,
    readAt:         row.read_at,
    dismissedAt:    row.dismissed_at,
  };
}

export interface CreateAlertInput {
  clinicianId: string;
  patientId: string | null;
  testResultId: string;
  testCode: string;
  patientName: string;
  severity: 'warning' | 'critical';
  message: string;
  triggeredScore: number | null;
}

/** Bulk-insert alert records. */
export async function createAlerts(alerts: CreateAlertInput[]): Promise<void> {
  if (!isSupabaseAvailable() || !supabase || alerts.length === 0) return;

  const rows = alerts.map((a) => ({
    clinician_id:   a.clinicianId,
    patient_id:     a.patientId,
    test_result_id: a.testResultId,
    test_code:      a.testCode,
    patient_name:   a.patientName,
    severity:       a.severity,
    message:        a.message,
    triggered_score: a.triggeredScore,
  }));

  const { error } = await supabase.from('clinical_alerts').insert(rows);
  if (error) console.error('Error creating clinical alerts:', error);
}

/**
 * Get active (non-dismissed) alerts for the given clinician.
 * Ordered by triggered_at DESC (newest first).
 */
export async function getActiveAlerts(clinicianId: string): Promise<ClinicalAlert[]> {
  if (!isSupabaseAvailable() || !supabase) return [];

  const { data, error } = await supabase
    .from('clinical_alerts')
    .select('*')
    .eq('clinician_id', clinicianId)
    .is('dismissed_at', null)
    .order('triggered_at', { ascending: false });

  if (error || !data) return [];
  return (data as AlertRow[]).map(dbToAlert);
}

/** Get alerts linked to a specific test result. */
export async function getAlertsByResultId(resultId: string): Promise<ClinicalAlert[]> {
  if (!isSupabaseAvailable() || !supabase) return [];

  const { data, error } = await supabase
    .from('clinical_alerts')
    .select('*')
    .eq('test_result_id', resultId)
    .order('triggered_at', { ascending: false });

  if (error || !data) return [];
  return (data as AlertRow[]).map(dbToAlert);
}

/** Mark all unread alerts as read for the given clinician. */
export async function markAllRead(clinicianId: string): Promise<void> {
  if (!isSupabaseAvailable() || !supabase) return;
  await supabase
    .from('clinical_alerts')
    .update({ read_at: new Date().toISOString() })
    .eq('clinician_id', clinicianId)
    .is('read_at', null)
    .is('dismissed_at', null);
}

/** Dismiss a single alert. */
export async function dismissAlert(alertId: string): Promise<void> {
  if (!isSupabaseAvailable() || !supabase) return;
  await supabase
    .from('clinical_alerts')
    .update({ dismissed_at: new Date().toISOString() })
    .eq('id', alertId);
}

/** Dismiss all active alerts for the given clinician. */
export async function dismissAllAlerts(clinicianId: string): Promise<void> {
  if (!isSupabaseAvailable() || !supabase) return;
  await supabase
    .from('clinical_alerts')
    .update({ dismissed_at: new Date().toISOString() })
    .eq('clinician_id', clinicianId)
    .is('dismissed_at', null);
}
